// Country data with codes, names, and flag emoji — the single source of truth for
// the codes stored in FootballPlayer.nationality/placeOfBirth (see the API's
// `Country` enum). Moved here from the admin's CountryAutocomplete so the web app
// can resolve stored codes back to a display name + flag.

export type CountryOption = {
  code: string;
  name: string;
  flag: string;
};

// Convert country code to flag emoji
export const countryCodeToFlag = (code: string): string => {
  // Handle special UK football nations
  if (code === 'ENG') return '🏴󠁧󠁢󠁥󠁮󠁧󠁿';
  if (code === 'SCO') return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';
  if (code === 'WAL') return '🏴󠁧󠁢󠁷󠁬󠁳󠁿';
  if (code === 'NIR') return '🇬🇧'; // Northern Ireland uses UK flag

  // Standard ISO country codes - convert to regional indicator symbols
  const codePoints = Array.from(code.toUpperCase()).map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export const COUNTRIES: CountryOption[] = [
  { code: 'AF', name: 'Afghanistan', flag: countryCodeToFlag('AF') },
  { code: 'AL', name: 'Albania', flag: countryCodeToFlag('AL') },
  { code: 'DZ', name: 'Algeria', flag: countryCodeToFlag('DZ') },
  { code: 'AD', name: 'Andorra', flag: countryCodeToFlag('AD') },
  { code: 'AO', name: 'Angola', flag: countryCodeToFlag('AO') },
  { code: 'AG', name: 'Antigua and Barbuda', flag: countryCodeToFlag('AG') },
  { code: 'AR', name: 'Argentina', flag: countryCodeToFlag('AR') },
  { code: 'AM', name: 'Armenia', flag: countryCodeToFlag('AM') },
  { code: 'AU', name: 'Australia', flag: countryCodeToFlag('AU') },
  { code: 'AT', name: 'Austria', flag: countryCodeToFlag('AT') },
  { code: 'AZ', name: 'Azerbaijan', flag: countryCodeToFlag('AZ') },
  { code: 'BS', name: 'Bahamas', flag: countryCodeToFlag('BS') },
  { code: 'BH', name: 'Bahrain', flag: countryCodeToFlag('BH') },
  { code: 'BD', name: 'Bangladesh', flag: countryCodeToFlag('BD') },
  { code: 'BB', name: 'Barbados', flag: countryCodeToFlag('BB') },
  { code: 'BY', name: 'Belarus', flag: countryCodeToFlag('BY') },
  { code: 'BE', name: 'Belgium', flag: countryCodeToFlag('BE') },
  { code: 'BZ', name: 'Belize', flag: countryCodeToFlag('BZ') },
  { code: 'BJ', name: 'Benin', flag: countryCodeToFlag('BJ') },
  { code: 'BT', name: 'Bhutan', flag: countryCodeToFlag('BT') },
  { code: 'BO', name: 'Bolivia', flag: countryCodeToFlag('BO') },
  { code: 'BA', name: 'Bosnia and Herzegovina', flag: countryCodeToFlag('BA') },
  { code: 'BW', name: 'Botswana', flag: countryCodeToFlag('BW') },
  { code: 'BR', name: 'Brazil', flag: countryCodeToFlag('BR') },
  { code: 'BN', name: 'Brunei', flag: countryCodeToFlag('BN') },
  { code: 'BG', name: 'Bulgaria', flag: countryCodeToFlag('BG') },
  { code: 'BF', name: 'Burkina Faso', flag: countryCodeToFlag('BF') },
  { code: 'BI', name: 'Burundi', flag: countryCodeToFlag('BI') },
  { code: 'CV', name: 'Cabo Verde', flag: countryCodeToFlag('CV') },
  { code: 'KH', name: 'Cambodia', flag: countryCodeToFlag('KH') },
  { code: 'CM', name: 'Cameroon', flag: countryCodeToFlag('CM') },
  { code: 'CA', name: 'Canada', flag: countryCodeToFlag('CA') },
  { code: 'CF', name: 'Central African Republic', flag: countryCodeToFlag('CF') },
  { code: 'TD', name: 'Chad', flag: countryCodeToFlag('TD') },
  { code: 'CL', name: 'Chile', flag: countryCodeToFlag('CL') },
  { code: 'CN', name: 'China', flag: countryCodeToFlag('CN') },
  { code: 'CO', name: 'Colombia', flag: countryCodeToFlag('CO') },
  { code: 'KM', name: 'Comoros', flag: countryCodeToFlag('KM') },
  { code: 'CG', name: 'Congo', flag: countryCodeToFlag('CG') },
  { code: 'CD', name: 'Congo (DRC)', flag: countryCodeToFlag('CD') },
  { code: 'CR', name: 'Costa Rica', flag: countryCodeToFlag('CR') },
  { code: 'CI', name: "Cote d'Ivoire", flag: countryCodeToFlag('CI') },
  { code: 'HR', name: 'Croatia', flag: countryCodeToFlag('HR') },
  { code: 'CU', name: 'Cuba', flag: countryCodeToFlag('CU') },
  { code: 'CY', name: 'Cyprus', flag: countryCodeToFlag('CY') },
  { code: 'CZ', name: 'Czechia', flag: countryCodeToFlag('CZ') },
  { code: 'DK', name: 'Denmark', flag: countryCodeToFlag('DK') },
  { code: 'DJ', name: 'Djibouti', flag: countryCodeToFlag('DJ') },
  { code: 'DM', name: 'Dominica', flag: countryCodeToFlag('DM') },
  { code: 'DO', name: 'Dominican Republic', flag: countryCodeToFlag('DO') },
  { code: 'EC', name: 'Ecuador', flag: countryCodeToFlag('EC') },
  { code: 'EG', name: 'Egypt', flag: countryCodeToFlag('EG') },
  { code: 'SV', name: 'El Salvador', flag: countryCodeToFlag('SV') },
  { code: 'GQ', name: 'Equatorial Guinea', flag: countryCodeToFlag('GQ') },
  { code: 'ER', name: 'Eritrea', flag: countryCodeToFlag('ER') },
  { code: 'EE', name: 'Estonia', flag: countryCodeToFlag('EE') },
  { code: 'SZ', name: 'Eswatini', flag: countryCodeToFlag('SZ') },
  { code: 'ET', name: 'Ethiopia', flag: countryCodeToFlag('ET') },
  { code: 'FJ', name: 'Fiji', flag: countryCodeToFlag('FJ') },
  { code: 'FI', name: 'Finland', flag: countryCodeToFlag('FI') },
  { code: 'FR', name: 'France', flag: countryCodeToFlag('FR') },
  { code: 'GA', name: 'Gabon', flag: countryCodeToFlag('GA') },
  { code: 'GM', name: 'Gambia', flag: countryCodeToFlag('GM') },
  { code: 'GE', name: 'Georgia', flag: countryCodeToFlag('GE') },
  { code: 'DE', name: 'Germany', flag: countryCodeToFlag('DE') },
  { code: 'GH', name: 'Ghana', flag: countryCodeToFlag('GH') },
  { code: 'GR', name: 'Greece', flag: countryCodeToFlag('GR') },
  { code: 'GD', name: 'Grenada', flag: countryCodeToFlag('GD') },
  { code: 'GT', name: 'Guatemala', flag: countryCodeToFlag('GT') },
  { code: 'GN', name: 'Guinea', flag: countryCodeToFlag('GN') },
  { code: 'GW', name: 'Guinea-Bissau', flag: countryCodeToFlag('GW') },
  { code: 'GY', name: 'Guyana', flag: countryCodeToFlag('GY') },
  { code: 'HT', name: 'Haiti', flag: countryCodeToFlag('HT') },
  { code: 'HN', name: 'Honduras', flag: countryCodeToFlag('HN') },
  { code: 'HU', name: 'Hungary', flag: countryCodeToFlag('HU') },
  { code: 'IS', name: 'Iceland', flag: countryCodeToFlag('IS') },
  { code: 'IN', name: 'India', flag: countryCodeToFlag('IN') },
  { code: 'ID', name: 'Indonesia', flag: countryCodeToFlag('ID') },
  { code: 'IR', name: 'Iran', flag: countryCodeToFlag('IR') },
  { code: 'IQ', name: 'Iraq', flag: countryCodeToFlag('IQ') },
  { code: 'IE', name: 'Ireland', flag: countryCodeToFlag('IE') },
  { code: 'IL', name: 'Israel', flag: countryCodeToFlag('IL') },
  { code: 'IT', name: 'Italy', flag: countryCodeToFlag('IT') },
  { code: 'JM', name: 'Jamaica', flag: countryCodeToFlag('JM') },
  { code: 'JP', name: 'Japan', flag: countryCodeToFlag('JP') },
  { code: 'JO', name: 'Jordan', flag: countryCodeToFlag('JO') },
  { code: 'KZ', name: 'Kazakhstan', flag: countryCodeToFlag('KZ') },
  { code: 'KE', name: 'Kenya', flag: countryCodeToFlag('KE') },
  { code: 'KI', name: 'Kiribati', flag: countryCodeToFlag('KI') },
  { code: 'KP', name: 'North Korea', flag: countryCodeToFlag('KP') },
  { code: 'KR', name: 'South Korea', flag: countryCodeToFlag('KR') },
  { code: 'KW', name: 'Kuwait', flag: countryCodeToFlag('KW') },
  { code: 'KG', name: 'Kyrgyzstan', flag: countryCodeToFlag('KG') },
  { code: 'LA', name: 'Laos', flag: countryCodeToFlag('LA') },
  { code: 'LV', name: 'Latvia', flag: countryCodeToFlag('LV') },
  { code: 'LB', name: 'Lebanon', flag: countryCodeToFlag('LB') },
  { code: 'LS', name: 'Lesotho', flag: countryCodeToFlag('LS') },
  { code: 'LR', name: 'Liberia', flag: countryCodeToFlag('LR') },
  { code: 'LY', name: 'Libya', flag: countryCodeToFlag('LY') },
  { code: 'LI', name: 'Liechtenstein', flag: countryCodeToFlag('LI') },
  { code: 'LT', name: 'Lithuania', flag: countryCodeToFlag('LT') },
  { code: 'LU', name: 'Luxembourg', flag: countryCodeToFlag('LU') },
  { code: 'MG', name: 'Madagascar', flag: countryCodeToFlag('MG') },
  { code: 'MW', name: 'Malawi', flag: countryCodeToFlag('MW') },
  { code: 'MY', name: 'Malaysia', flag: countryCodeToFlag('MY') },
  { code: 'MV', name: 'Maldives', flag: countryCodeToFlag('MV') },
  { code: 'ML', name: 'Mali', flag: countryCodeToFlag('ML') },
  { code: 'MT', name: 'Malta', flag: countryCodeToFlag('MT') },
  { code: 'MH', name: 'Marshall Islands', flag: countryCodeToFlag('MH') },
  { code: 'MR', name: 'Mauritania', flag: countryCodeToFlag('MR') },
  { code: 'MU', name: 'Mauritius', flag: countryCodeToFlag('MU') },
  { code: 'MX', name: 'Mexico', flag: countryCodeToFlag('MX') },
  { code: 'FM', name: 'Micronesia', flag: countryCodeToFlag('FM') },
  { code: 'MD', name: 'Moldova', flag: countryCodeToFlag('MD') },
  { code: 'MC', name: 'Monaco', flag: countryCodeToFlag('MC') },
  { code: 'MN', name: 'Mongolia', flag: countryCodeToFlag('MN') },
  { code: 'ME', name: 'Montenegro', flag: countryCodeToFlag('ME') },
  { code: 'MA', name: 'Morocco', flag: countryCodeToFlag('MA') },
  { code: 'MZ', name: 'Mozambique', flag: countryCodeToFlag('MZ') },
  { code: 'MM', name: 'Myanmar', flag: countryCodeToFlag('MM') },
  { code: 'NA', name: 'Namibia', flag: countryCodeToFlag('NA') },
  { code: 'NR', name: 'Nauru', flag: countryCodeToFlag('NR') },
  { code: 'NP', name: 'Nepal', flag: countryCodeToFlag('NP') },
  { code: 'NL', name: 'Netherlands', flag: countryCodeToFlag('NL') },
  { code: 'NZ', name: 'New Zealand', flag: countryCodeToFlag('NZ') },
  { code: 'NI', name: 'Nicaragua', flag: countryCodeToFlag('NI') },
  { code: 'NE', name: 'Niger', flag: countryCodeToFlag('NE') },
  { code: 'NG', name: 'Nigeria', flag: countryCodeToFlag('NG') },
  { code: 'MK', name: 'North Macedonia', flag: countryCodeToFlag('MK') },
  { code: 'NO', name: 'Norway', flag: countryCodeToFlag('NO') },
  { code: 'OM', name: 'Oman', flag: countryCodeToFlag('OM') },
  { code: 'PK', name: 'Pakistan', flag: countryCodeToFlag('PK') },
  { code: 'PW', name: 'Palau', flag: countryCodeToFlag('PW') },
  { code: 'PS', name: 'Palestine', flag: countryCodeToFlag('PS') },
  { code: 'PA', name: 'Panama', flag: countryCodeToFlag('PA') },
  { code: 'PG', name: 'Papua New Guinea', flag: countryCodeToFlag('PG') },
  { code: 'PY', name: 'Paraguay', flag: countryCodeToFlag('PY') },
  { code: 'PE', name: 'Peru', flag: countryCodeToFlag('PE') },
  { code: 'PH', name: 'Philippines', flag: countryCodeToFlag('PH') },
  { code: 'PL', name: 'Poland', flag: countryCodeToFlag('PL') },
  { code: 'PT', name: 'Portugal', flag: countryCodeToFlag('PT') },
  { code: 'QA', name: 'Qatar', flag: countryCodeToFlag('QA') },
  { code: 'RO', name: 'Romania', flag: countryCodeToFlag('RO') },
  { code: 'RU', name: 'Russia', flag: countryCodeToFlag('RU') },
  { code: 'RW', name: 'Rwanda', flag: countryCodeToFlag('RW') },
  { code: 'KN', name: 'Saint Kitts and Nevis', flag: countryCodeToFlag('KN') },
  { code: 'LC', name: 'Saint Lucia', flag: countryCodeToFlag('LC') },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: countryCodeToFlag('VC') },
  { code: 'WS', name: 'Samoa', flag: countryCodeToFlag('WS') },
  { code: 'SM', name: 'San Marino', flag: countryCodeToFlag('SM') },
  { code: 'ST', name: 'Sao Tome and Principe', flag: countryCodeToFlag('ST') },
  { code: 'SA', name: 'Saudi Arabia', flag: countryCodeToFlag('SA') },
  { code: 'SN', name: 'Senegal', flag: countryCodeToFlag('SN') },
  { code: 'RS', name: 'Serbia', flag: countryCodeToFlag('RS') },
  { code: 'SC', name: 'Seychelles', flag: countryCodeToFlag('SC') },
  { code: 'SL', name: 'Sierra Leone', flag: countryCodeToFlag('SL') },
  { code: 'SG', name: 'Singapore', flag: countryCodeToFlag('SG') },
  { code: 'SK', name: 'Slovakia', flag: countryCodeToFlag('SK') },
  { code: 'SI', name: 'Slovenia', flag: countryCodeToFlag('SI') },
  { code: 'SB', name: 'Solomon Islands', flag: countryCodeToFlag('SB') },
  { code: 'SO', name: 'Somalia', flag: countryCodeToFlag('SO') },
  { code: 'ZA', name: 'South Africa', flag: countryCodeToFlag('ZA') },
  { code: 'SS', name: 'South Sudan', flag: countryCodeToFlag('SS') },
  { code: 'ES', name: 'Spain', flag: countryCodeToFlag('ES') },
  { code: 'LK', name: 'Sri Lanka', flag: countryCodeToFlag('LK') },
  { code: 'SD', name: 'Sudan', flag: countryCodeToFlag('SD') },
  { code: 'SR', name: 'Suriname', flag: countryCodeToFlag('SR') },
  { code: 'SE', name: 'Sweden', flag: countryCodeToFlag('SE') },
  { code: 'CH', name: 'Switzerland', flag: countryCodeToFlag('CH') },
  { code: 'SY', name: 'Syria', flag: countryCodeToFlag('SY') },
  { code: 'TW', name: 'Taiwan', flag: countryCodeToFlag('TW') },
  { code: 'TJ', name: 'Tajikistan', flag: countryCodeToFlag('TJ') },
  { code: 'TZ', name: 'Tanzania', flag: countryCodeToFlag('TZ') },
  { code: 'TH', name: 'Thailand', flag: countryCodeToFlag('TH') },
  { code: 'TL', name: 'Timor-Leste', flag: countryCodeToFlag('TL') },
  { code: 'TG', name: 'Togo', flag: countryCodeToFlag('TG') },
  { code: 'TO', name: 'Tonga', flag: countryCodeToFlag('TO') },
  { code: 'TT', name: 'Trinidad and Tobago', flag: countryCodeToFlag('TT') },
  { code: 'TN', name: 'Tunisia', flag: countryCodeToFlag('TN') },
  { code: 'TR', name: 'Turkey', flag: countryCodeToFlag('TR') },
  { code: 'TM', name: 'Turkmenistan', flag: countryCodeToFlag('TM') },
  { code: 'TV', name: 'Tuvalu', flag: countryCodeToFlag('TV') },
  { code: 'UG', name: 'Uganda', flag: countryCodeToFlag('UG') },
  { code: 'UA', name: 'Ukraine', flag: countryCodeToFlag('UA') },
  { code: 'AE', name: 'United Arab Emirates', flag: countryCodeToFlag('AE') },
  { code: 'GB', name: 'United Kingdom', flag: countryCodeToFlag('GB') },
  { code: 'US', name: 'United States', flag: countryCodeToFlag('US') },
  { code: 'UY', name: 'Uruguay', flag: countryCodeToFlag('UY') },
  { code: 'UZ', name: 'Uzbekistan', flag: countryCodeToFlag('UZ') },
  { code: 'VU', name: 'Vanuatu', flag: countryCodeToFlag('VU') },
  { code: 'VA', name: 'Vatican City', flag: countryCodeToFlag('VA') },
  { code: 'VE', name: 'Venezuela', flag: countryCodeToFlag('VE') },
  { code: 'VN', name: 'Vietnam', flag: countryCodeToFlag('VN') },
  { code: 'YE', name: 'Yemen', flag: countryCodeToFlag('YE') },
  { code: 'ZM', name: 'Zambia', flag: countryCodeToFlag('ZM') },
  { code: 'ZW', name: 'Zimbabwe', flag: countryCodeToFlag('ZW') },
  // UK constituent countries (for football purposes)
  { code: 'ENG', name: 'England', flag: countryCodeToFlag('ENG') },
  { code: 'SCO', name: 'Scotland', flag: countryCodeToFlag('SCO') },
  { code: 'WAL', name: 'Wales', flag: countryCodeToFlag('WAL') },
  { code: 'NIR', name: 'Northern Ireland', flag: countryCodeToFlag('NIR') },
];

// Create a map for quick lookup by code
export const COUNTRY_BY_CODE: Record<string, CountryOption> = COUNTRIES.reduce(
  (acc, country) => {
    acc[country.code] = country;
    return acc;
  },
  {} as Record<string, CountryOption>
);
